import React, { useEffect, useState } from 'react';
import { Box, Button, MenuItem, TextField, Typography, Paper } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { DepartmentService } from '../../services/department.service';
import { AuthService } from '../../services/auth.service';
import {
    Department, DepartmentTreeNodeDTO, DepartmentUnitType, DEPARTMENT_UNIT_TYPES,
    DEPARTMENT_UNIT_TYPE_LABEL, DEPARTMENT_UNIT_TYPE_RANK,
} from '../../interfaces/department.interface';
import { useToast } from '../../context/ToastContext';
import OrgUnitPicker, { collectSubtreeIds, flattenOrgUnitTree } from '../common/OrgUnitPicker';

const DepartmentForm: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const existingDepartment = location.state?.department as Department | undefined;
    const institutionId = AuthService.getSelectedInstitution() || '';

    const [formData, setFormData] = useState({
        name: existingDepartment?.name || '',
        description: existingDepartment?.description || '',
        unitType: (existingDepartment?.unitType || 'DEPARTMENT') as DepartmentUnitType,
        parentId: existingDepartment?.parentId ?? null as string | null,
        code: existingDepartment?.code || '',
        sortOrder: existingDepartment?.sortOrder ?? 0,
        costCentreCode: existingDepartment?.costCentreCode || '', // B4-H
    });
    const toast = useToast();

    // A unit re-parented under its own descendant is rejected by the backend with a 400 (cycle) --
    // hide the whole subtree from the parent picker rather than letting the user pick it and fail
    // on submit. Seeded with just the unit's own id so it's excluded immediately, then widened
    // once the tree loads.
    const [excludeIds, setExcludeIds] = useState<string[]>(existingDepartment ? [existingDepartment.id] : []);

    // Tracks the CURRENTLY SELECTED parent's rank so handleUnitTypeChange can tell whether it's
    // still valid under a newly chosen type -- OrgUnitPicker's onChange hands us the whole option
    // (with unitType) on a fresh pick; for an existing department loaded with a parent already set,
    // we look its rank up once the tree arrives below.
    const [parentUnitType, setParentUnitType] = useState<DepartmentUnitType | null>(null);

    useEffect(() => {
        if (!existingDepartment || !institutionId) return;
        let cancelled = false;
        DepartmentService.getDepartmentTree(institutionId)
            .then(tree => {
                if (cancelled) return;
                const subtree = collectSubtreeIds(tree, existingDepartment.id);
                setExcludeIds(subtree.length ? subtree : [existingDepartment.id]);
                if (existingDepartment.parentId) {
                    const parent = flattenOrgUnitTree(tree as DepartmentTreeNodeDTO[])
                        .find(o => o.id === existingDepartment.parentId);
                    setParentUnitType(parent?.unitType ?? null);
                }
            })
            .catch(() => { /* parent picker still excludes the unit's own id on failure */ });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingDepartment?.id, institutionId]);

    // A DEPARTMENT (rank 0) may never have a parent; every other type may still be a root, but
    // when it does have one, the parent's rank must be strictly lower than the child's (spec §2).
    const parentTypes = DEPARTMENT_UNIT_TYPES.filter(
        t => DEPARTMENT_UNIT_TYPE_RANK[t] < DEPARTMENT_UNIT_TYPE_RANK[formData.unitType]
    );

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const dto = {
                name: formData.name,
                description: formData.description,
                unitType: formData.unitType,
                parentId: formData.unitType === 'DEPARTMENT' ? null : formData.parentId,
                code: formData.code.trim() ? formData.code.trim() : null,
                sortOrder: formData.sortOrder,
                // B4-H: sent only when the field was changed (absent = the server keeps the stored code; blank clears it)
                ...(formData.costCentreCode.trim() !== (existingDepartment?.costCentreCode ?? '').trim()
                    ? { costCentreCode: formData.costCentreCode.trim() } : {}),
            };
            if (existingDepartment) {
                await DepartmentService.updateDepartment(existingDepartment.id, dto);
            } else {
                await DepartmentService.createDepartment(dto);
            }
            toast.showSuccess(existingDepartment ? 'Department updated' : 'Department created');
            navigate('/departments');
        } catch (err) {
            toast.showApiError(err, 'Failed to save department');
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleUnitTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const unitType = e.target.value as DepartmentUnitType;
        // A DEPARTMENT can't have a parent, and any other type's parent must have a strictly lower
        // rank than the newly chosen type (spec §2) -- clear a selection that's now invalid (or
        // unverifiable, i.e. the parent's rank isn't known yet) rather than silently keeping it.
        const parentStillValid = unitType !== 'DEPARTMENT'
            && parentUnitType !== null
            && DEPARTMENT_UNIT_TYPE_RANK[parentUnitType] < DEPARTMENT_UNIT_TYPE_RANK[unitType];
        setFormData(prev => ({
            ...prev,
            unitType,
            parentId: parentStillValid ? prev.parentId : null,
        }));
        if (!parentStillValid) setParentUnitType(null);
    };

    return (
        <Box sx={{ padding: 3 }} data-help-page="department.form.page">
            <Paper sx={{ padding: 3, maxWidth: 600, margin: '0 auto' }}>
                <Typography variant="h5" sx={{ marginBottom: 3 }}>
                    {existingDepartment ? 'Edit Department' : 'Create Department'}
                </Typography>
                <form onSubmit={handleSubmit} data-help-section="department.form.section.details">
                    <TextField
                        fullWidth
                        label="Name"
                        data-help="department.form.field.name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        inputProps={{ maxLength: 100 }}
                        sx={{ marginBottom: 2 }}
                    />
                    <TextField
                        fullWidth
                        select
                        label="Type"
                        data-help="department.form.field.unitType"
                        name="unitType"
                        value={formData.unitType}
                        onChange={handleUnitTypeChange}
                        sx={{ marginBottom: 2 }}
                    >
                        {DEPARTMENT_UNIT_TYPES.map(t => (
                            <MenuItem key={t} value={t}>{DEPARTMENT_UNIT_TYPE_LABEL[t]}</MenuItem>
                        ))}
                    </TextField>
                    {formData.unitType !== 'DEPARTMENT' && (
                        <Box sx={{ marginBottom: 2 }} data-help="department.form.field.parent">
                            <OrgUnitPicker
                                institutionId={institutionId}
                                value={formData.parentId}
                                onChange={option => {
                                    setFormData(prev => ({ ...prev, parentId: option?.id ?? null }));
                                    setParentUnitType(option?.unitType ?? null);
                                }}
                                types={parentTypes}
                                excludeIds={existingDepartment ? excludeIds : undefined}
                                label="Parent"
                                placeholder="Search parent unit... (optional)"
                                fullWidth
                                data-testid="department-form-parent-picker"
                            />
                        </Box>
                    )}
                    <TextField
                        fullWidth
                        label="Code"
                        data-help="department.form.field.code"
                        name="code"
                        value={formData.code}
                        onChange={handleChange}
                        inputProps={{ maxLength: 32 }}
                        sx={{ marginBottom: 2 }}
                    />
                    {/* ---- B4-H cost centre ---- */}
                    <TextField
                        fullWidth
                        label="Cost-centre code"
                        data-help="department.form.field.costCentre"
                        name="costCentreCode"
                        value={formData.costCentreCode}
                        onChange={handleChange}
                        helperText="Optional. Journal and budget imports match this code first, then the department name."
                        inputProps={{ maxLength: 32 }}
                        sx={{ marginBottom: 2 }}
                    />
                    {/* ---- end B4-H ---- */}
                    <TextField
                        fullWidth
                        type="number"
                        label="Sort Order"
                        data-help="department.form.field.sortOrder"
                        name="sortOrder"
                        value={formData.sortOrder}
                        onChange={e => setFormData(prev => ({ ...prev, sortOrder: parseInt(e.target.value, 10) || 0 }))}
                        sx={{ marginBottom: 2 }}
                    />
                    <TextField
                        fullWidth
                        label="Description"
                        data-help="department.form.field.description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        multiline
                        rows={4}
                        sx={{ marginBottom: 3 }}
                    />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            type="submit"
                            data-help="department.form.save"
                            variant="contained"
                            color="primary"
                        >
                            {existingDepartment ? 'Update' : 'Create'}
                        </Button>
                        <Button
                            variant="outlined"
                            data-help="department.form.cancel"
                            onClick={() => navigate('/departments')}
                        >
                            Cancel
                        </Button>
                    </Box>
                </form>
            </Paper>
        </Box>
    );
};

export default DepartmentForm;
